/**
 * COMPTABILITÉ — LA FICHE D'UNE NUIT.
 *
 * Tout ce qui concerne une nuit commerciale, au même endroit et dans l'ordre
 * où on s'en sert : la soirée et le geste (commencer ou reprendre la clôture),
 * LE TOTAL DE LA NUIT façon classeur de caisse, ce que dit le rapport ZAP et
 * ce que dit Shotgun du guichet de l'Accueil (côte à côte quand l'écran le
 * permet), les rapports qui composent la nuit, et le récap des annexes.
 *
 * ═══ LE MÊME BANDEAU QUE L'ASSISTANT ═══
 *
 * « Le top serait d'avoir le même design quand on sélectionne une soirée,
 * juste avant de cliquer sur clôture » (l'exploitant, 07/10/2026). La fiche
 * reprend donc `BandeauSoiree` (ClosingHero.tsx) : visuel de la soirée
 * flouté en fond, vignette nette, la nuit en surtitre, la soirée en titre,
 * son état, les gestes à droite — Exporter CSV, Ignorer la clôture, puis
 * Commencer ou Reprendre en bouton principal — et les chiffres clés de la nuit.
 *
 * ═══ LE TOTAL DE LA NUIT ═══
 *
 * « Un vrai total CA avec écart total », sur le modèle du classeur que
 * l'exploitante remplit chaque nuit (`FeuilleDeCaisse`). Il lit la CLÔTURE
 * quand elle existe (`GET /finance/closings/:id`, le compté et les coupures
 * n'existent que là) ; sans clôture, le rapport seul, colonne compté vide, et
 * l'invitation à la commencer. Les montants suivent `finance.source.read`,
 * comme partout sur cette fiche : sans lui, la clôture n'est pas même lue.
 *
 * ═══ CE QUE ZAP DIT N'EST PAS ENCORE UN CHIFFRE ARRÊTÉ ═══
 *
 * Les montants du rapport sont ceux qu'il ANNONCE (bars et vestiaire, hors
 * offerts) : une SOURCE, que la clôture confronte au comptage. Le chiffre qui
 * fait foi est le RETENU d'une clôture finalisée, dans la clôture.
 *
 * ═══ IGNORER LA CLÔTURE ═══
 *
 * Une nuit d'avant Gestion se VALIDE en bloc depuis le bandeau (« Ignorer la
 * clôture », `NightValidation.tsx`) : chiffre d'affaires et entrées arrêtés
 * sans clôture détaillée. Le bandeau le dit ensuite (« Validée sans clôture le
 * … par … »), et le bloc « Validée sans clôture » montre ce qui a été arrêté.
 *
 * ═══ LES VENTES DU GUICHET D'UNE AUTRE NUIT (08/10/2026) ═══
 *
 * La caisse Shotgun n°2 restée ouverte sur la soirée du 26/09 a vendu 110
 * billets pendant la nuit du 03/10. Les deux fiches le disent, en tête, avec
 * la phrase du serveur, l'état de la décision, le lien vers l'autre nuit et
 * le chemin vers l'étape Accueil où l'on décide. Le panneau « Accueil ·
 * Shotgun » ne mélange plus ses sources : « Vendu au guichet » est la
 * billetterie de la NUIT (la reddition seulement à défaut, et c'est écrit),
 * la concordance dit le SENS de l'écart, et le compté met de côté les ventes
 * faites ici sur une autre soirée.
 *
 * ═══ ÉCARTER UN RAPPORT ═══
 *
 * Un rapport émis pendant un entretien de caisse ne décrit aucune nuit réelle.
 * On l'ÉCARTE, avec un motif ; il reste visible, barré, et se rétablit d'un
 * geste. Rien n'est supprimé : un document source est une preuve.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { ArrowLeftRight, ArrowRight, Ban, FileText, MoonStar, Plus } from 'lucide-react'
import { Link, navigate } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { Checkbox } from '../admin/form.js'
import { formatEcart, formatMoney, tonSeuil } from './money.js'
import {
  CLOSING_STATUS_LABELS, CLOSING_STATUS_PILL, EnCours, Indisponible, heureParis, ilYA, nuitLongue,
  type ClosingDetail, type Etat,
} from './ClosingCommon.js'
import {
  EtatNuit, jourMois, lireClotureDeNuit, lireFicheNuit, montantNuit, phraseConcordance, urlCloture,
  type NightAccueilRow, type NightDetailResponse, type NightRedditionControlRow, type NightRedditionRow,
  type NightReportRow, type NightSoiree,
} from './nights-common.js'
import { BarreEcart, Ecarter, useSelectionDocs } from './SourcesEcart.js'
import { DeplacerRapport, instantParis } from './DeplacerRapport.js'
import { VoirSource } from './SourceViewer.js'
import { NightRecap } from './NightRecap.js'
import { IgnorerCloture } from './NightValidation.js'
import { BoutonExportCsv } from '../admin/BoutonExportCsv.js'
import { urlExportCsvNuit } from '../admin/telechargement.js'
import { ouDansLeClasseur, repriseDe } from './reprise-historique.js'
import { nombre } from './entrees.js'
import { BandeauSoiree, Chiffre } from './ClosingHero.js'
import { FeuilleDeCaisse, useSeuilsFinance } from './FeuilleDeCaisse.js'
import { TraceRemplacement, ValidationRemplacee } from './ValidationRemplacee.js'
import { BilletterieAssociations } from './BilletterieAssociations.js'
import {
  aideCompte, aideEcart, aideRapport, chiffreAffairesDuBandeau, feuilleDeCaisse, REPRISE_SANS_DETAIL,
  type FeuilleCaisse, type NuitAvecPreventes,
} from './night-sheet-model.js'
import './finance-wizard.css'
import './finance-bandeau.css'
import './finance-nights.css'
import './finance-guichet.css'

/** Les colonnes du rapport ZAP, dans l'ordre où la clôture les compte. */
const COLONNES: Array<{ code: string; libelle: string }> = [
  { code: 'CB', libelle: 'Carte' },
  { code: 'ESPECES', libelle: 'Espèces' },
  { code: 'OFFERT', libelle: 'Offerts' },
]

export function FinanceNightPage(props: { date: string }) {
  const toast = useToast()
  const { permissions } = usePermissions()
  const peutOuvrir = permissions.includes('finance.closure.create')
  const peutEcarter = permissions.includes('finance.source.manage')
  const peutLireBilletterie = permissions.includes('finance.live.read')
  const peutLireSources = permissions.includes('finance.source.read')
  const peutLireOfferts = permissions.includes('finance.offers.read')
  const peutBanque = permissions.includes('finance.bank.read')
  const seuils = useSeuilsFinance()

  const [nuit, setNuit] = useState<Etat<NightDetailResponse>>({ etat: 'chargement' })
  const [ouverture, setOuverture] = useState(false)
  const selection = useSelectionDocs()

  const charger = useCallback(() => {
    // La même lecture que les cartes de l'accueil (`lireFicheNuit`) : la carte
    // et la fiche écrivent les mêmes chiffres.
    void lireFicheNuit(props.date).then(setNuit)
  }, [props.date])
  useEffect(charger, [charger])

  /* La clôture de la nuit, pour le compté et les coupures : la fiche ne les
     porte pas. Relue quand sa version change (une écriture ailleurs). Sans le
     droit de lire les montants, elle n'est pas lue du tout. */
  const resumeCloture = nuit.etat === 'ok' ? nuit.valeur.closing : null
  const [cloture, setCloture] = useState<Etat<ClosingDetail> | null>(null)
  useEffect(() => {
    if (!resumeCloture || !peutLireSources) { setCloture(null); return }
    let vivant = true
    void lireClotureDeNuit(resumeCloture.id).then((r) => { if (vivant) setCloture(r) })
    return () => { vivant = false }
  }, [resumeCloture?.id, resumeCloture?.version, peutLireSources]) // eslint-disable-line react-hooks/exhaustive-deps

  const commencer = async () => {
    setOuverture(true)
    const r = await adminMutate<{ error?: string; id?: string; message?: string }>(
      'POST', '/api/v1/finance/closings', { business_date: props.date },
    )
    setOuverture(false)
    if (r.status >= 200 && r.status < 300 && typeof r.data.id === 'string') {
      toast('ok', 'Clôture ouverte. L’assistant reprendra exactement où vous vous arrêterez.')
      navigate(urlCloture(props.date, nuit.etat === 'ok' ? nuit.valeur.soirees : null))
      return
    }
    toast('error', r.data.message ?? 'La clôture n’a pas pu être ouverte.')
  }

  if (nuit.etat !== 'ok') {
    return (
      <main className="lc-page lc-page--large lc-stagger fn-page">
        {/* Le parent logique vient du registre (`canonicalParentFor`) : on arrive
            ici du calendrier, de l'accueil ou de l'assistant, et un libellé
            figé « Calendrier des nuits » mentait dans deux cas sur trois. */}
        <SmartBack />
        {nuit.etat === 'chargement' ? <EnCours quoi="de la nuit" /> : (
          <Indisponible titre="Cette nuit n’a pas pu être lue" message={nuit.message}>
            <button className="lc-btn lc-btn--sm" onClick={charger}>Réessayer</button>
          </Indisponible>
        )}
      </main>
    )
  }

  const n = nuit.valeur
  const principaux = n.reports.filter((r) => r.role === 'PRINCIPAL')
  const complements = n.reports.filter((r) => r.role === 'COMPLEMENT')
  const ecartes = n.documents.filter((d) => d.ignored)
  const annexes = n.documents.filter((d) => d.report_type === 'ZAP_OTHER' && !d.ignored)
  const ouvrable = n.state !== 'A_VENIR'
  // Ce qui peut se sélectionner : les rapports ZAP, les redditions, les écartés.
  const etatDe = new Map<string, boolean>([
    ...n.reports.map((r) => [r.document_id, false] as const),
    ...n.redditions.map((r) => [r.document_id, r.ignored] as const),
    ...ecartes.map((d) => [d.id, true] as const),
  ])
  const choisis = [...selection.ids].filter((id) => etatDe.has(id)).map((id) => ({ id, ecarte: etatDe.get(id)! }))
  const caseDe = (id: string, libelle: string) => peutEcarter ? (
    <Checkbox checked={selection.a(id)} onChange={() => selection.basculer(id)} ariaLabel={`Sélectionner ${libelle}`} />
  ) : null

  /* ─── le total de la nuit ─── */
  // La clôture fait foi dès qu'elle existe et qu'elle est lue ; tant qu'elle
  // se lit, la feuille attend plutôt que d'afficher le rapport seul et de
  // basculer une seconde plus tard.
  const clotureLue = cloture?.etat === 'ok' ? cloture.valeur : null
  const feuilleEnAttente = n.closing !== null && peutLireSources && cloture?.etat !== 'ok' && cloture?.etat !== 'absent'
  const f = feuilleDeCaisse(clotureLue, n as NuitAvecPreventes)
  // Une nuit REPRISE DE L'HISTORIQUE, sans clôture : son chiffre est arrêté
  // (le Total du suivi de la DAF), ses caisses ne sont pas dans Gestion. Rien
  // n'y est à faire — une clôture détaillée reste possible, en geste
  // secondaire : en bouton principal, elle se lisait comme une tâche en retard.
  // La feuille en décide (`reprise`) : une nuit reprise dont les rapports ZAP
  // SONT dans Gestion (septembre 2026) n'est pas « sans détail », et sa
  // clôture reste le geste principal, comme une nuit validée à la main. Sans
  // le droit de lire les sources, la fiche arrive SANS caisses ZAP
  // (`ficheDeNuitRendue`) : la feuille ne peut pas trancher, on ne dit rien.
  const repriseSansCloture = n.closing === null && peutLireSources && f.reprise

  const geste: ReactNode = n.closing !== null ? (
    <Link className="lc-btn lc-btn--primary" href={urlCloture(n.business_date, n.soirees)}>
      {n.closing.status === 'CLOSED' ? 'Voir la clôture' : 'Reprendre la clôture'} <ArrowRight size={16} aria-hidden="true" />
    </Link>
  ) : ouvrable && peutOuvrir ? (
    <button className={repriseSansCloture ? 'lc-btn' : 'lc-btn lc-btn--primary'} disabled={ouverture} onClick={() => void commencer()}>
      <Plus size={16} aria-hidden="true" />
      {ouverture ? 'Ouverture en cours' : repriseSansCloture ? 'Ouvrir une clôture détaillée' : 'Commencer la clôture'}
    </button>
  ) : null

  return (
    <main className="lc-page lc-page--large lc-stagger fn-page">
      {/* ═══════════ la soirée : le bandeau de l'assistant ═══════════ */}
      <div className="fnb-cadre">
        <BandeauNuit seuils={seuils} nuit={n} feuille={peutLireSources && !feuilleEnAttente ? f : null} cloture={clotureLue}
          gestes={(
            <>
              {/* Le CSV de la nuit (24/10/2026), fabriqué par le serveur sous les droits de cette fiche. */}
              <BoutonExportCsv variante="ghost" url={urlExportCsvNuit(n.business_date)} secours="comptabilite-nuit.csv"
                title="Le total de la nuit, son chiffre d’affaires et ses sources, pour Excel" />
              <IgnorerCloture date={n.business_date} onDone={charger} />
              {geste}
            </>
          )}
        />
      </div>

      {n.flag_labels.length > 0 ? (
        <ul className="fn-nuit__drapeaux fn-nuit__drapeaux--bandeau" aria-label="Points d’attention">
          {n.flag_labels.map((x) => <li key={x}><span className="fn-etat fn-etat--warning">{x}</span></li>)}
        </ul>
      ) : null}

      {/* ═══════════ les ventes du guichet d'une autre nuit ═══════════ */}
      <VentesHorsNuit nuit={n} accueilPosId={clotureLue?.off_night?.accueil_pos_id ?? null} />

      {/* ═══════════ validée en bloc ═══════════ */}
      {n.validation !== null && repriseDe(n.validation.history) !== null ? (
        <PanneauReprise validation={n.validation} />
      ) : n.validation !== null ? (
        <section className="lc-panel">
          <div className="lc-panel__head">
            <h2 className="lc-panel__title">Validée sans clôture détaillée</h2>
          </div>
          <div className="lc-panel__body">
            {/* Ce que la validation en bloc a arrêté — le montant suit
                le droit de lire les montants : « — » sans lui. */}
            <div className="fn-kpis">
              <div>
                <p className="fi-kpi__label">Chiffre d’affaires validé</p>
                <p className="fn-kpi__valeur">{montantNuit(n.validation.revenue_cents)}</p>
              </div>
              <div>
                <p className="fi-kpi__label">Entrées validées</p>
                <p className="fn-kpi__valeur">{n.validation.entries === null ? '—' : nombre(n.validation.entries)}</p>
              </div>
            </div>
            <p className="lc-hint">
              Validée en bloc, sans clôture détaillée{n.validation.reason ? ` — « ${n.validation.reason} »` : ''}.
              Une clôture détaillée reste possible : une fois finalisée, c’est elle qui fera foi.
            </p>
          </div>
        </section>
      ) : null}

      {/* ═══════════ la validation qu'une clôture en cours remplacerait ═══════════
          (16/10/2026) La nuit validée en bloc et clôturée au détail : ce que
          la finalisation fera du chiffre validé, dit AVANT, ici comme dans
          la synthèse de la clôture — la même phrase, servie par le serveur. */}
      {clotureLue !== null && (clotureLue.status === 'DRAFT' || clotureLue.status === 'READY') && clotureLue.validation_replacement ? (
        <section className="lc-panel" aria-label="Validation remplacée à la finalisation">
          <div className="lc-panel__body">
            <ValidationRemplacee r={clotureLue.validation_replacement} />
          </div>
        </section>
      ) : clotureLue?.replaced_validation ? (
        // Finalisée : la validation en bloc n'est plus affichée nulle part
        // ailleurs. Le chiffre validé, la billetterie non reprise et le motif
        // restent lisibles ici, sans passer par le journal d'audit.
        <section className="lc-panel" aria-label="Validation remplacée par la finalisation">
          <div className="lc-panel__body">
            <TraceRemplacement t={clotureLue.replaced_validation} />
          </div>
        </section>
      ) : null}

      {/* ═══════════ le total de la nuit, façon classeur ═══════════ */}
      {!peutLireSources ? (
        <section className="lc-panel">
          <div className="lc-panel__body">
            <p className="fi-empty">Votre rôle ne permet pas de lire les montants de la nuit.</p>
          </div>
        </section>
      ) : feuilleEnAttente ? (
        <section className="lc-panel"><div className="lc-panel__body"><EnCours quoi="du total de la nuit" /></div></section>
      ) : (
        <FeuilleDeCaisse
          feuille={f}
          seuils={seuils}
          offertsVisibles={peutLireOfferts}
          lienBanque={peutBanque && clotureLue !== null}
          invitation={n.closing !== null ? (cloture?.etat === 'absent' ? (
            <p className="fi-reserve">La clôture n’a pas pu être lue : {cloture.message} Le tableau montre le rapport seul.</p>
          ) : null) : (
            <div className="fn-invitation">
              <p className="lc-hint">
                {!ouvrable
                  ? 'Cette nuit n’a pas encore eu lieu : elle se clôturera une fois terminée.'
                  : repriseSansCloture
                    ? `${REPRISE_SANS_DETAIL}. Son chiffre d’affaires est celui du suivi de la DAF ; une clôture détaillée reste possible, et finalisée, c’est elle qui fera foi.`
                    : n.validation !== null
                    ? 'Aucune clôture détaillée n’est ouverte pour cette nuit.'
                    : 'La clôture reprendra ces montants comme rapport, à confronter au comptage de chaque point de vente.'}
              </p>
              {ouvrable && !peutOuvrir ? <p className="lc-hint">Votre rôle ne permet pas d’ouvrir une clôture.</p> : null}
              {geste}
            </div>
          )}
        />
      )}

      {/* ═══════════ la billetterie des associations (Pretix), 16/10/2026 ═══════════
          Une clôture finalisée redit ce qu'elle a FIGÉ ; sinon la fiche, en direct. */}
      {/* Ce que la nuit a déjà fait de son chiffre (revue du 16/10) : validée
          (reprise de la DAF ou en bloc), ou finalisée avant le gel — le
          panneau ne le devine pas depuis la billetterie seule. Une clôture
          finalisée pas encore lue : rien plutôt qu'une phrase peut-être fausse. */}
      {n.closing?.status === 'CLOSED' && clotureLue === null ? null : (
        <BilletterieAssociations
          a={clotureLue?.associations?.frozen ? clotureLue.associations : n.associations}
          peutRegler={permissions.includes('finance.reference.read')}
          peutVoirSoirees={permissions.includes('soiree.read')}
          situation={n.closing?.status === 'CLOSED'
            ? (clotureLue?.associations?.frozen ? null : 'FINALISEE_SANS')
            : n.validation !== null ? (repriseDe(n.validation.history) !== null ? 'REPRISE' : 'VALIDEE') : null}
        />
      )}

      {/* ═══════════ ce que ZAP annonce, ce que dit le guichet : côte à côte ═══════════ */}
      <div className="fn-duo">
        <section className="lc-panel">
          <div className="lc-panel__head">
            <h2 className="lc-panel__title">Rapport de caisse ZAP</h2>
          </div>
          <div className="lc-panel__body">
            {n.reports.length === 0 ? (
              <p className="fi-empty">
                Aucun rapport ZAP pour cette nuit.
                {repriseSansCloture
                  ? ' Nuit reprise de l’historique : ses rapports de caisse ne sont pas dans Gestion, aucun n’est attendu.'
                  : n.soirees.some((s) => s.status === 'SCHEDULED')
                    ? ' Une soirée a pourtant eu lieu : le rapport arrive en général le matin, parfois plus tard.'
                    : ''}
              </p>
            ) : (
              <>
                <div className="fn-kpis">
                  <div>
                    <p className="fi-kpi__label">Annoncé par ZAP · hors offerts</p>
                    <p className="fn-kpi__valeur">{montantNuit(n.zap.announced_cents)}</p>
                  </div>
                  <div>
                    <p className="fi-kpi__label">Offerts (Carte Serveur)</p>
                    <p className="fn-kpi__valeur">{montantNuit(n.zap.offers_cents)}</p>
                  </div>
                  <div>
                    <p className="fi-kpi__label">Rapports</p>
                    <p className="fn-kpi__valeur">
                      {principaux.length} principal{complements.length > 0 ? ` + ${complements.length} complément${complements.length > 1 ? 's' : ''}` : ''}
                    </p>
                  </div>
                </div>
                <p className="lc-hint">
                  Ce sont les montants du rapport : une SOURCE, que la clôture confronte au comptage. Le
                  chiffre qui fait foi est celui retenu à la clôture.
                </p>

                {n.by_point_of_sale.length > 0 ? (
                  <div className="fi-tablewrap fn-tableau-cadre">
                    <table className="fi-table fi-table--chiffres fn-table-zap">
                      <colgroup>
                        <col />
                        {COLONNES.map((c) => <col key={c.code} className="fi-col-montant" />)}
                        <col className="fi-col-montant" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th scope="col">Point de vente</th>
                          {COLONNES.map((c) => <th key={c.code} scope="col" className="fi-money">{c.libelle}</th>)}
                          <th scope="col" className="fi-money">Annoncé</th>
                        </tr>
                      </thead>
                      <tbody>
                        {n.by_point_of_sale.map((p) => (
                          <tr key={p.point_of_sale_id ?? `alias-${p.name}`}>
                            <th scope="row">
                              <span className="fi-cell-titre">{p.name}</span>
                              {p.resolved ? null : <span className="fi-cell-alerte">caisse du rapport à rattacher</span>}
                            </th>
                            {COLONNES.map((c) => (
                              <td key={c.code} className="fi-money">{montantNuit(p.by_method[c.code] ?? null)}</td>
                            ))}
                            <td className="fi-money fi-money--fort">{montantNuit(p.announced_cents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}

                {n.unresolved_registers.length > 0 ? (
                  <p className="fi-reserve">
                    Caisses du rapport sans correspondance dans Gestion : {n.unresolved_registers.join(', ')}.
                    Rattachez-les depuis le document du rapport ; les rapports suivants se résoudront seuls.
                  </p>
                ) : null}
              </>
            )}
          </div>
        </section>

        <AccueilShotgun
          accueil={n.accueil} soirees={n.soirees} peutLire={peutLireBilletterie}
          redditions={n.redditions} controle={n.controle_reddition} peutEcarter={peutEcarter}
          caseDe={caseDe} onChange={charger}
        />
      </div>

      {/* ═══════════ les rapports qui composent la nuit ═══════════ */}
      {n.reports.length > 0 || ecartes.length > 0 ? (
        <section className="lc-panel">
          <div className="lc-panel__head">
            <h2 className="lc-panel__title">Rapports reçus</h2>
          </div>
          <div className="lc-panel__body">
            <ul className="fn-rapports">
              {n.reports.map((r) => (
                <Rapport key={r.document_id} rapport={r} nuit={n.business_date} peutEcarter={peutEcarter} onChange={charger}
                  case={caseDe(r.document_id, r.role === 'PRINCIPAL' ? 'le rapport principal' : 'le complément')} />
              ))}
              {ecartes.filter((d) => d.report_type !== 'ZAP_OTHER' && d.report_type !== 'SHOTGUN_EVENT_REPORT').map((d) => (
                <li key={d.id} className="fn-rapport fn-rapport--ecarte">
                  <div className="fn-rapport__tete">
                    {caseDe(d.id, d.filename)}
                    <div>
                      <VoirSource className="fv-lien fn-rapport__nom" doc={{ id: d.id, filename: d.filename, provider: d.provider, report_type: d.report_type, business_date: n.business_date, received_at: d.received_at }} />
                      <p className="fn-rapport__meta">Écarté : {d.ignored_reason ?? 'motif non transmis'}</p>
                    </div>
                  </div>
                  <span className="fn-etat fn-etat--idle"><Ban size={14} aria-hidden="true" /><span>Écarté</span></span>
                  {peutEcarter ? <Ecarter documentId={d.id} retablir onChange={charger} /> : <span />}
                </li>
              ))}
            </ul>

            {annexes.length > 0 ? (
              <details className="fn-annexes">
                <summary>
                  {annexes.length} annexe{annexes.length > 1 ? 's' : ''} ZAP (détail par caisse, doseurs, TVA, produits) — conservées comme preuves
                </summary>
                <ul>
                  {annexes.map((a) => (
                    <li key={a.id}>
                      <FileText size={14} aria-hidden="true" />{' '}
                      <VoirSource doc={{ id: a.id, filename: a.filename, provider: a.provider, report_type: a.report_type, business_date: n.business_date, received_at: a.received_at }}
                        liste={annexes.map((x) => ({ id: x.id, filename: x.filename, provider: x.provider, report_type: x.report_type, business_date: n.business_date, received_at: x.received_at }))} />
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* Le récap des annexes ZAP : offerts, doses, bouteilles, tiroirs,
          produits — à lire si l'on veut, sans ouvrir de clôture. */}
      {peutLireSources ? <NightRecap nuit={n.business_date} /> : null}

      <BarreEcart choisis={choisis} onVider={selection.vider} onFait={charger} />
    </main>
  )
}

/* ═══════════════════════ le bandeau de la fiche ═══════════════════════ */


/**
 * UNE NUIT REPRISE DE L'HISTORIQUE (phase 1, décision du 15/10/2026) — son
 * chiffre est le « Total » du suivi de la DAF, pas une validation faite ici :
 * le panneau le dit, et dit où le relire dans le classeur. Le montant suit le
 * droit de lire les montants, comme pour une nuit validée à l'écran.
 */
function PanneauReprise(props: { validation: NonNullable<NightDetailResponse['validation']> }) {
  const v = props.validation
  const r = repriseDe(v.history)!
  return (
    <section className="lc-panel">
      <div className="lc-panel__head">
        <h2 className="lc-panel__title">Repris de l’historique</h2>
      </div>
      <div className="lc-panel__body">
        <div className="fn-kpis">
          <div>
            <p className="fi-kpi__label">Chiffre d’affaires repris (Total DAF)</p>
            <p className="fn-kpi__valeur">{montantNuit(v.revenue_cents)}</p>
          </div>
          <div>
            <p className="fi-kpi__label">Entrées reprises (hors guestlist)</p>
            <p className="fn-kpi__valeur">{v.entries === null ? '—' : nombre(v.entries)}</p>
          </div>
        </div>
        <p className="lc-hint">
          {r.label}. Chiffre repris du suivi de la DAF, {ouDansLeClasseur(r)} : billetterie reversée
          + bars et vestiaire comptés, offerts exclus. Aucune clôture détaillée : une clôture
          finalisée plus tard fera foi.
        </p>
        {r.date_correction ? (
          // La ligne indiquée porte une autre date que la nuit : on le dit, pour
          // que l'écart se lise comme une correction voulue, pas comme une erreur.
          <p className="lc-hint">Date corrigée : {r.date_correction}.</p>
        ) : null}
      </div>
    </section>
  )
}
/**
 * LE BANDEAU DE LA NUIT : celui de l'assistant (`BandeauSoiree`), avec les
 * chiffres de la feuille de caisse — rapport, compté, écart total, chiffre
 * d'affaires — et les entrées quand la clôture les porte. `feuille` vaut
 * `null` sans le droit de lire les montants (ou pendant la lecture de la
 * clôture) : chaque chiffre s'écrit alors « — ».
 *
 * Les aides sont celles du modèle, partagées avec l'assistant (« compté en
 * entier », « hors N sans réponse », « rapport attendu »), et le chiffre
 * d'affaires est celui qui FAIT FOI : l'arrêté d'une clôture finalisée, le
 * validé d'une nuit validée en bloc, sinon le compté ou les rapports
 * (`chiffreAffairesDuBandeau`, revue du 07/10/2026).
 */
function BandeauNuit(props: { nuit: NightDetailResponse; feuille: FeuilleCaisse | null; cloture: ClosingDetail | null; gestes: ReactNode; seuils: { warn: number; alert: number } }) {
  const { nuit: n, feuille: f } = props
  const maintenues = n.soirees.filter((s) => s.status === 'SCHEDULED')
  const affichees = maintenues.length > 0 ? maintenues : n.soirees
  const premiere = maintenues[0] ?? null
  const annulee = maintenues.length === 0 && n.soirees.length > 0
  const c = n.closing
  const e = formatEcart(f?.total.ecart ?? null)
  const entrees = props.cloture?.entries ?? null
  const ca = f === null ? null : chiffreAffairesDuBandeau(f, n.validation)

  return (
    <BandeauSoiree
      soiree={premiere}
      icone={MoonStar}
      surtitre={affichees.length > 0 ? `Nuit du ${nuitLongue(n.business_date).toLowerCase()}` : 'Nuit sans soirée déclarée'}
      titre={affichees.length > 0 ? affichees.map((s) => (s.status === 'CANCELLED' ? `${s.name} (annulée)` : s.name)).join(' · ') : nuitLongue(n.business_date)}
      etat={(
        <>
          <EtatNuit state={n.state} libelle={n.state_label} />
          {/* Une clôture finalisée se lit déjà dans l'état de la nuit (« Clôturée »). */}
          {c && c.status !== 'CLOSED' ? <span className={CLOSING_STATUS_PILL[c.status]}>Clôture · {CLOSING_STATUS_LABELS[c.status].toLowerCase()}</span> : null}
          {c && c.revision > 1 ? <span className="lc-pill lc-pill--idle">Révision {c.revision}</span> : null}
          {annulee ? <span className="lc-pill lc-pill--idle">Soirée annulée</span> : null}
        </>
      )}
      meta={c && (c.last_editor_name || c.last_edited_at) ? (
        <span>
          Clôture modifiée{c.last_editor_name ? ` par ${c.last_editor_name}` : ''}{c.last_edited_at ? `, ${ilYA(c.last_edited_at)}` : ''}
        </span>
      ) : maintenues.length > 0 ? (
        <span>Soirée à {heureParis(maintenues[0]!.starts_at)}{maintenues.length > 1 ? ` · ${maintenues.length} soirées cette nuit` : ''}</span>
      ) : undefined}
      actions={props.gestes}
      chiffresClasse="fnb-chiffres"
      chiffres={(
        <>
          <Chiffre
            libelle="Rapport"
            valeur={formatMoney(f?.total.rapport ?? null)}
            aide={f === null ? 'Montants non lus' : aideRapport(f)}
          />
          <Chiffre
            libelle="Compté"
            valeur={formatMoney(f?.total.compte ?? null)}
            aide={f === null ? 'Montants non lus' : aideCompte(f)}
          />
          <Chiffre
            libelle="Écart total"
            valeur={e.texte}
            ton={f?.total.ecart == null ? undefined : `fi-seuil--${tonSeuil(f.total.ecart, props.seuils)}`}
            aide={f === null ? 'Montants non lus' : !f.avecCloture ? 'Se calcule au comptage' : aideEcart(f)}
          />
          <Chiffre
            libelle="Chiffre d’affaires"
            valeur={formatMoney(ca?.valeur ?? null)}
            aide={ca === null ? 'Montants non lus' : ca.aide}
          />
          {entrees ? (
            <Chiffre
              libelle="Entrées"
              valeur={nombre(entrees.total)}
              aide={`${nombre(entrees.shotgun_scanned)} billets scannés · guestlist ${entrees.guestlist_count === null ? 'à saisir' : nombre(entrees.guestlist_count)}`}
            />
          ) : null}
        </>
      )}
    />
  )
}

/**
 * LES VENTES DU GUICHET D'UNE AUTRE NUIT, SUR LA FICHE — la phrase du serveur
 * (sans montant : elle part avec la nuit), l'état, le montant (avec le droit
 * de lire la billetterie, sinon « — »), et les deux chemins : l'autre nuit, et
 * l'étape Accueil de la clôture d'ici, où l'on décide.
 */
function VentesHorsNuit(props: { nuit: NightDetailResponse; accueilPosId: string | null }) {
  const n = props.nuit
  const cas = n.off_night_sales ?? []
  if (cas.length === 0) return null
  const etape = n.closing ? `${urlCloture(n.business_date, n.soirees)}${props.accueilPosId ? `/${props.accueilPosId}` : ''}` : null
  return (
    <section className="lc-panel" aria-labelledby="fn-hors-nuit">
      <div className="lc-panel__head">
        <span className="fih-head__icon" aria-hidden="true"><ArrowLeftRight size={17} /></span>
        <h2 id="fn-hors-nuit" className="lc-panel__title">Ventes du guichet d’une autre nuit</h2>
      </div>
      <div className="lc-panel__body fgh-fiche">
        {cas.map((c) => (
          <article key={c.key} className={`fgh-cas fgh-cas--${c.status === 'EN_ATTENTE' ? 'attente' : c.status === 'RATTACHE' ? 'rattache' : 'maintenu'}`}>
            <div className="fgh-cas__tete">
              <span className={c.status === 'EN_ATTENTE' ? 'lc-pill lc-pill--warning' : c.status === 'RATTACHE' ? 'lc-pill lc-pill--ok' : 'lc-pill'}>{c.status_label}</span>
              <span className="lc-hint">{c.classe_label}</span>
            </div>
            <p className="fgh-cas__phrase">{c.sentence}</p>
            <dl className="fgh-chiffres">
              <div>
                <dt>Vendus hors de leur nuit</dt>
                <dd className="fi-money fi-money--fort">{montantNuit(c.total_cents)}</dd>
              </div>
              <div>
                <dt>Dont TPE</dt>
                <dd className="fi-money">{montantNuit(c.card_present_cents)}</dd>
              </div>
              <div>
                <dt>Billets</dt>
                <dd>
                  {c.tickets}
                  <span className="fgh-chiffres__sous">
                    {c.paid_tickets} payant{c.paid_tickets > 1 ? 's' : ''}{c.free_tickets > 0 ? `, ${c.free_tickets} exonération${c.free_tickets > 1 ? 's' : ''}` : ''}
                  </span>
                </dd>
              </div>
            </dl>
            {c.revision ? <p className="fi-provisoire fgh-revision">{c.revision.message}</p> : null}
            <div className="fgh-gestes">
              {etape ? (
                <Link className="lc-btn lc-btn--sm" href={etape}>
                  {c.status === 'EN_ATTENTE' ? 'Décider à l’étape Accueil' : 'Voir à l’étape Accueil'} <ArrowRight size={14} aria-hidden="true" />
                </Link>
              ) : (
                <span className="lc-hint">La décision se prend à l’étape Accueil de la clôture : de cette nuit, ou de l’autre.</span>
              )}
              <Link className="fgh-lien" href={`/comptabilite/nuits/${c.other_business_date}`}>
                Voir la nuit du {jourMois(c.other_business_date)}
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

function Rapport(props: { rapport: NightReportRow; nuit: string; peutEcarter: boolean; onChange: () => void; case: ReactNode }) {
  const r = props.rapport
  const role = r.role === 'PRINCIPAL' ? 'Rapport principal' : 'Complément hors ligne'
  // Une période sur plusieurs nuits se lit avec ses jours : « 15:35 → 10:01 »
  // cachait que la caisse était restée ouverte du mercredi au vendredi.
  const couvertes = r.nights_covered ?? [props.nuit]
  const multi = couvertes.length > 1
  const periode = r.period_start && r.period_end
    ? multi ? `${instantParis(r.period_start)} → ${instantParis(r.period_end)}` : `${heureParis(r.period_start)} → ${heureParis(r.period_end)}`
    : null
  return (
    <li className="fn-rapport">
      <div className="fn-rapport__tete">
        {props.case}
        <div>
          <VoirSource className="fv-lien fn-rapport__nom" doc={{ id: r.document_id, filename: r.filename, provider: 'ZAP_SIS', report_type: 'ZAP_REGISTER_SUMMARY', received_at: r.received_at, libelle: role }}>{role}</VoirSource>
          <p className="fn-rapport__meta">
            {periode ? `Période ${periode} · ` : ''}reçu {heureParis(r.received_at)}
            {r.duplicate_of ? ' · même contenu qu’un complément déjà compté : non additionné' : ''}
            {r.reading_status === 'FAILED' ? ' · lecture impossible : montant inconnu' : ''}
          </p>
          {multi ? (
            <p className="fn-rapport__meta fn-rapport__multi">
              Cette période couvre {couvertes.length} nuits.{props.peutEcarter ? ' Si ses ventes sont celles d’une autre nuit : « Déplacer ».' : ''}
            </p>
          ) : null}
        </div>
      </div>
      <span className="fi-money"><strong>{montantNuit(r.announced_cents)}</strong></span>
      {props.peutEcarter ? (
        <div className="fn-rapport__gestes">
          <DeplacerRapport documentId={r.document_id} nuit={props.nuit} couvertes={couvertes}
            periode={r.period_start && r.period_end ? { debut: r.period_start, fin: r.period_end } : null}
            onChange={props.onChange} />
          <Ecarter documentId={r.document_id} onChange={props.onChange} />
        </div>
      ) : <span />}
    </li>
  )
}

/**
 * L'ACCUEIL — la caisse d'entrée, c'est Shotgun. UN bloc, pas deux : le
 * guichet (relevé billet par billet) donne la répartition TPE / espèces que le
 * personnel compte en face ; la reddition (le relevé figé que Shotgun émet)
 * donne le total, les préventes, les frais et le solde. Montrer les deux
 * totaux côte à côte disait deux fois la même chose (retour du 07/10/2026) :
 * on montre le chiffre, et on dit s'ils concordent.
 */
function AccueilShotgun(props: {
  accueil: NightAccueilRow
  soirees: NightSoiree[]
  peutLire: boolean
  redditions: NightRedditionRow[]
  controle: NightRedditionControlRow | null
  peutEcarter: boolean
  caseDe: (id: string, libelle: string) => ReactNode
  onChange: () => void
}) {
  const { accueil, peutLire, redditions, controle: c } = props
  const maintenue = props.soirees.some((s) => s.status === 'SCHEDULED')
  if (!maintenue && accueil.onsite === null && redditions.length === 0) return null
  const o = accueil.onsite
  // Les redditions qui COMPTENT, comme pour la feuille (`redditionsRetenues`) :
  // ni écartées à la main, ni illisibles, ni doublon, ni version remplacée —
  // additionnées une seule fois.
  const retenues = redditions.filter((r) => !r.ignored && r.parse_status !== 'FAILED' && !r.set_aside_by)
  const somme = (k: 'online_ttc_cents' | 'fees_ttc_cents' | 'balance_cents'): number | null =>
    retenues.length > 0 && retenues.every((r) => r[k] !== null) ? retenues.reduce((t, r) => t + r[k]!, 0) : null
  // « Vendu au guichet » et « dont TPE / espèces » viennent de la MÊME source :
  // les billets vendus pendant la nuit. Ils mêlaient la reddition (4 820 €) et
  // les billets (TPE 4 920 €) : deux chiffres qui se contredisaient (26/09).
  // La reddition ne prend la place qu'à défaut, et le libellé le dit.
  const parBillets = o !== null
  const surPlace = parBillets ? o.facial_cents : c?.reddition_onsite_cents ?? null
  // Les ventes faites PENDANT cette nuit sur la soirée d'une autre sont dans la
  // caisse, et dans aucune reddition d'ici : le compté les met de côté.
  const entrant = c?.hors_nuit_entrant_cents ?? 0
  return (
    <section className="lc-panel">
      <div className="lc-panel__head">
        <h2 className="lc-panel__title">Accueil · Shotgun</h2>
      </div>
      <div className="lc-panel__body">
        {!peutLire && retenues.length === 0 ? (
          <p className="fi-empty">Votre rôle ne permet pas de lire les ventes de la billetterie.</p>
        ) : (
          <>
            <div className="fn-kpis">
              <div>
                <p className="fi-kpi__label">{parBillets ? 'Vendu au guichet cette nuit' : 'Vendu au guichet · reddition'}</p>
                <p className="fn-kpi__valeur">{montantNuit(surPlace)}</p>
                {o && o.tickets > 0 ? (
                  <p className="lc-hint">{o.tickets} billets{o.free_tickets > 0 ? ` dont ${o.free_tickets} exonéré${o.free_tickets > 1 ? 's' : ''}` : ''}</p>
                ) : null}
              </div>
              <div>
                <p className="fi-kpi__label">dont TPE</p>
                <p className="fn-kpi__valeur">{o && o.coverage === 'COMPLETE' ? montantNuit(o.by_method.card_present_cents) : '—'}</p>
              </div>
              <div>
                <p className="fi-kpi__label">dont espèces</p>
                <p className="fn-kpi__valeur">{o && o.coverage === 'COMPLETE' ? montantNuit(o.by_method.cash_cents) : '—'}</p>
              </div>
              <div>
                <p className="fi-kpi__label">Compté à l’Accueil</p>
                <p className="fn-kpi__valeur">{c?.comptage_accueil_cents === null || !c ? 'Pas encore' : montantNuit(c.comptage_accueil_cents)}</p>
                <Ecart ecart={entrant > 0 ? (c?.ecart_comptage_net_cents ?? null) : (c?.ecart_comptage_cents ?? null)} accord="Conforme à la reddition" />
                {/* Un contrôle PHYSIQUE de la caisse face à la reddition : l'argent
                    vendu cette nuit est dans cette caisse quelle que soit la
                    décision comptable — d'où « quelle que soit leur décision ». */}
                {entrant > 0 && c?.comptage_accueil_cents != null ? (
                  <p className="lc-hint">
                    Contrôle de caisse, ventes faites cette nuit : hors {montantNuit(entrant)} vendus sur la soirée d’une autre nuit, quelle que soit leur décision
                  </p>
                ) : null}
              </div>
            </div>

            {retenues.length > 0 ? (
              <div className="fn-kpis fn-kpis--second">
                <div>
                  <p className="fi-kpi__label">Préventes en ligne · TTC</p>
                  <p className="fn-kpi__valeur fn-kpi__valeur--second">{montantNuit(somme('online_ttc_cents'))}</p>
                </div>
                <div>
                  <p className="fi-kpi__label">Frais Shotgun · TTC</p>
                  <p className="fn-kpi__valeur fn-kpi__valeur--second">{montantNuit(somme('fees_ttc_cents'))}</p>
                </div>
                <div>
                  <p className="fi-kpi__label">Solde reversé par Shotgun</p>
                  <p className="fn-kpi__valeur fn-kpi__valeur--second">{montantNuit(somme('balance_cents'))}</p>
                </div>
              </div>
            ) : null}

            <p className="fn-concordance">{phraseConcordance(c, retenues.length > 0, accueil.settled)}</p>
          </>
        )}

        {redditions.length > 0 ? (
          <ul className="fn-rapports">
            {redditions.map((r) => (
              <li key={r.document_id} className={`fn-rapport${r.ignored ? ' fn-rapport--ecarte' : ''}`}>
                <div className="fn-rapport__tete">
                  {props.caseDe(r.document_id, r.report_id ?? r.filename)}
                  <div>
                    <VoirSource className="fv-lien fn-rapport__nom"
                      doc={{ id: r.document_id, filename: r.filename, provider: 'SHOTGUN', report_type: 'SHOTGUN_EVENT_REPORT', libelle: `Reddition Shotgun${r.report_id ? ` ${r.report_id}` : ''}` }}>
                      Reddition {r.report_id ?? r.filename}
                    </VoirSource>
                    <p className="fn-rapport__meta">
                      {r.ignored
                        ? `Écartée : ${r.ignored_reason ?? 'motif non transmis'}`
                        : r.parse_status === 'FAILED'
                          ? 'Illisible : pas additionnée'
                          : r.set_aside_by
                          ? `Pas additionnée : ${r.set_aside_by} compte déjà pour cette soirée`
                          : `${r.issued_on ? `Émise le ${r.issued_on.split('-').reverse().join('/')}` : 'Date d’émission non lue'}${r.event_name ? ` · ${r.event_name}` : ''}`}
                    </p>
                  </div>
                </div>
                <span className="fi-money"><strong>{montantNuit(r.sales_ttc_cents)}</strong></span>
                {props.peutEcarter ? <Ecarter documentId={r.document_id} retablir={r.ignored} onChange={props.onChange} /> : <span />}
              </li>
            ))}
          </ul>
        ) : null}

        {o && o.caveats.length > 0 ? (
          <details className="fn-reserves-plis">
            <summary>Ce que ces chiffres ne disent pas</summary>
            <ul className="fn-reserves">
              {o.caveats.map((x) => <li key={x} className="lc-hint">{x}</li>)}
            </ul>
          </details>
        ) : null}
      </div>
    </section>
  )
}

/** Un écart dit en clair : accord, ou le montant et son sens. `null` = un des deux termes manque. */
function Ecart(props: { ecart: number | null; accord: string }) {
  if (props.ecart === null) return null
  if (props.ecart === 0) return <p className="fn-ecart fn-ecart--ok">{props.accord}</p>
  return (
    <p className="fn-ecart fn-ecart--warning">
      Écart de {montantNuit(Math.abs(props.ecart))} {props.ecart > 0 ? 'en plus' : 'en moins'}
    </p>
  )
}
